/**
 * Case Form Component
 * Create and edit legal cases
 */

import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Save, X, Briefcase, FileText, Gavel, User } from 'lucide-react'

import { useCase, useUpdateCase } from '@/hooks/useCases'
import { toast } from '@/lib/toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FormSkeleton } from '@/components/loading'

// Validation schema
const caseSchema = z.object({
  case_type: z.number().int().min(1).optional().nullable(),
  title: z.string().min(1, 'Title is required').max(150),
  case_number: z.string().max(100).optional().nullable().or(z.literal('')),
  place_of_detention: z.string().max(200).optional().nullable().or(z.literal('')),
  court_pending: z.string().max(100).optional().nullable().or(z.literal('')),
  status: z.number().int().min(1).max(5).optional().nullable(),
  court_code: z.string().max(50).optional().nullable().or(z.literal('')),
  nature: z.string().max(500).optional().nullable().or(z.literal('')),
  action_taken: z.string().max(500).optional().nullable().or(z.literal('')),
  adverse: z.string().optional().nullable().or(z.literal('')), // JSON string
  other_details: z.string().optional().nullable().or(z.literal('')), // JSON string
  user_id: z.string().uuid().optional().nullable().or(z.literal('')),
  visit_transaction_id: z.string().uuid().optional().nullable().or(z.literal('')),
})

type CaseFormData = z.infer<typeof caseSchema>

export function CaseForm() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  // Fetch existing case
  const { data: caseData, isLoading: isLoadingCase } = useCase(id!)

  // Mutation
  const updateCase = useUpdateCase()

  // Form setup
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm<CaseFormData>({
    resolver: zodResolver(caseSchema),
    defaultValues: {
      case_type: undefined,
      title: '',
      case_number: '',
      place_of_detention: '',
      court_pending: '',
      status: undefined,
      court_code: '',
      nature: '',
      action_taken: '',
      adverse: '',
      other_details: '',
      user_id: '',
      visit_transaction_id: '',
    },
  })

  // Load existing case data
  useEffect(() => {
    if (caseData) {
      setValue('case_type', caseData.case_type || undefined)
      setValue('title', caseData.title || '')
      setValue('case_number', caseData.case_number || '')
      setValue('place_of_detention', caseData.place_of_detention || '')
      setValue('court_pending', caseData.court_pending || '')
      setValue('status', caseData.status ? (Number(caseData.status) || undefined) : undefined)
      setValue('court_code', caseData.court_code || '')
      setValue('nature', caseData.nature || '')
      setValue('action_taken', caseData.action_taken || '')
      setValue('adverse', caseData.adverse ? JSON.stringify(caseData.adverse, null, 2) : '')
      setValue('other_details', caseData.other_details ? JSON.stringify(caseData.other_details, null, 2) : '')
      setValue('user_id', caseData.user_id || '')
      setValue('visit_transaction_id', caseData.visit_transaction_id || '')
    }
  }, [caseData, setValue])

  // Submit handler
  const onSubmit = async (data: CaseFormData) => {
    try {
      // Convert empty strings to null and parse JSON fields
      const cleanData: any = Object.fromEntries(
        Object.entries(data).map(([key, value]) => {
          if (value === '') return [key, null]

          // Parse JSON fields
          if (key === 'adverse' || key === 'other_details') {
            if (!value) return [key, null]
            try {
              return [key, JSON.parse(value as string)]
            } catch (e) {
              throw new Error(`Invalid JSON in ${key === 'adverse' ? 'Adverse Information' : 'Other Details'}`)
            }
          }

          return [key, value]
        })
      )

      await updateCase.mutateAsync({ id: id!, data: cleanData })
      toast.success('Case updated successfully!')
      navigate('/cases')
    } catch (error: any) {
      toast.error(
        'Failed to update case',
        error.response?.data?.detail || error.message
      )
    }
  }

  if (isLoadingCase) {
    return <FormSkeleton fields={12} />
  }

  const status = watch('status')

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-primary">Edit Case</h1>
        <p className="text-gray-600 mt-1">Update case information below</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Basic Information */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
            <Briefcase className="h-5 w-5" />
            Basic Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Title */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="title">
                Case Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                {...register('title')}
                placeholder="Brief description of the case"
                className={errors.title ? 'border-red-500' : ''}
              />
              {errors.title && (
                <p className="text-sm text-red-500">{errors.title.message}</p>
              )}
            </div>

            {/* Case Number */}
            <div className="space-y-2">
              <Label htmlFor="case_number">Case Number</Label>
              <Input
                id="case_number"
                {...register('case_number')}
                placeholder="e.g., CRIM-2024-001"
              />
            </div>

            {/* Case Type */}
            <div className="space-y-2">
              <Label htmlFor="case_type">Case Type</Label>
              <Input
                type="number"
                id="case_type"
                {...register('case_type', { valueAsNumber: true })}
                placeholder="Enter case type ID"
              />
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={status?.toString() || ''}
                onValueChange={(value) => setValue('status', value ? Number(value) : undefined)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Pending</SelectItem>
                  <SelectItem value="2">In Progress</SelectItem>
                  <SelectItem value="3">Resolved</SelectItem>
                  <SelectItem value="4">Dismissed</SelectItem>
                  <SelectItem value="5">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Nature */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="nature">Nature of Case</Label>
              <Textarea
                id="nature"
                {...register('nature')}
                placeholder="Brief description of the nature of the case"
                rows={3}
                className={errors.nature ? 'border-red-500' : ''}
              />
              {errors.nature && (
                <p className="text-sm text-red-500">{errors.nature.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Court Information */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
            <Gavel className="h-5 w-5" />
            Court Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Court Pending */}
            <div className="space-y-2">
              <Label htmlFor="court_pending">Court Pending</Label>
              <Input
                id="court_pending"
                {...register('court_pending')}
                placeholder="e.g., RTC Manila Branch 12"
              />
            </div>

            {/* Court Code */}
            <div className="space-y-2">
              <Label htmlFor="court_code">Court Code</Label>
              <Input
                id="court_code"
                {...register('court_code')}
                placeholder="e.g., RTC-MNL-12"
              />
            </div>

            {/* Place of Detention */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="place_of_detention">Place of Detention</Label>
              <Input
                id="place_of_detention"
                {...register('place_of_detention')}
                placeholder="If applicable"
              />
            </div>
          </div>
        </div>

        {/* Case Details */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
            <FileText className="h-5 w-5" />
            Case Details
          </h2>
          <div className="space-y-4">
            {/* Action Taken */}
            <div className="space-y-2">
              <Label htmlFor="action_taken">Action Taken</Label>
              <Textarea
                id="action_taken"
                {...register('action_taken')}
                placeholder="Describe actions taken on this case"
                rows={4}
              />
            </div>

            {/* Adverse Information (JSON) */}
            <div className="space-y-2">
              <Label htmlFor="adverse">
                Adverse Information (JSON)
                <span className="text-sm text-gray-500 ml-2 font-normal">
                  Optional - Enter valid JSON
                </span>
              </Label>
              <Textarea
                id="adverse"
                {...register('adverse')}
                placeholder='{"key": "value"}'
                rows={4}
                className={`font-mono text-sm ${errors.adverse ? 'border-red-500' : ''}`}
              />
              {errors.adverse && (
                <p className="text-sm text-red-500">{errors.adverse.message}</p>
              )}
              <p className="text-xs text-gray-500">
                Example: {`{"opponent": "John Doe", "charges": ["Theft", "Fraud"]}`}
              </p>
            </div>

            {/* Other Details (JSON) */}
            <div className="space-y-2">
              <Label htmlFor="other_details">
                Other Details (JSON)
                <span className="text-sm text-gray-500 ml-2 font-normal">
                  Optional - Enter valid JSON
                </span>
              </Label>
              <Textarea
                id="other_details"
                {...register('other_details')}
                placeholder='{"key": "value"}'
                rows={4}
                className={`font-mono text-sm ${errors.other_details ? 'border-red-500' : ''}`}
              />
              {errors.other_details && (
                <p className="text-sm text-red-500">{errors.other_details.message}</p>
              )}
              <p className="text-xs text-gray-500">
                Example: {`{"notes": "Important information", "tags": ["urgent", "priority"]}`}
              </p>
            </div>
          </div>
        </div>

        {/* Assignment Information */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
            <User className="h-5 w-5" />
            Assignment Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Assigned Lawyer (User ID) */}
            <div className="space-y-2">
              <Label htmlFor="user_id">Assigned Lawyer (User ID)</Label>
              <Input
                id="user_id"
                {...register('user_id')}
                placeholder="UUID of assigned lawyer"
                className={errors.user_id ? 'border-red-500' : ''}
              />
              {errors.user_id && (
                <p className="text-sm text-red-500">{errors.user_id.message}</p>
              )}
            </div>

            {/* Visit Transaction ID */}
            <div className="space-y-2">
              <Label htmlFor="visit_transaction_id">Visit Transaction ID</Label>
              <Input
                id="visit_transaction_id"
                {...register('visit_transaction_id')}
                placeholder="UUID of related visit"
                className={errors.visit_transaction_id ? 'border-red-500' : ''}
              />
              {errors.visit_transaction_id && (
                <p className="text-sm text-red-500">{errors.visit_transaction_id.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/cases')}
          >
            <X className="mr-2 h-4 w-4" />
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="bg-primary hover:bg-primary/90"
          >
            <Save className="mr-2 h-4 w-4" />
            {isSubmitting ? 'Saving...' : 'Update Case'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default CaseForm
